// Coalesces single-answer fetches into one POST /answers/bulk call per tick.
// Scrolling mounts answers in bursts, and each burst used to fan out into
// per-answer GET /answers/:id requests (channel reconciliation, straggler
// hydration) costing a full API roundtrip apiece.
import api from 'api/api'
import * as cache from 'state/cache'
import { Answer } from 'types/answers'
import { preserveSignalContextForBatchedAnswer } from './answerBatchSignalContext'

const FLUSH_DELAY_MS = 16
const MAX_BATCH_SIZE = 100

type PendingResolver = {
    resolve: (answer: Answer | null) => void
    reject: (error: unknown) => void
}

// id -> resolvers waiting on it. An id stays in this map from queueing until
// its batch response lands, so late callers join the in-flight request.
const pendingResolvers = new Map<string, PendingResolver[]>()
// ids queued but not yet flushed into a request
const queuedIds = new Set<string>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

// The show endpoint derives signal settings from an explicit quest context the
// client passes along (see api.getAnswer); the bulk endpoint falls back to the
// answer's own quest. Keep a previously cached signal context sticky so a
// batched refresh cannot flip vote routing for answers viewed via streams.
function withPreservedSignalContext(answer: Answer): Answer {
    return preserveSignalContextForBatchedAnswer(answer, cache.getCachedAnswer(String(answer.id)))
}

function settle(id: string, settler: (resolver: PendingResolver) => void) {
    const resolvers = pendingResolvers.get(id)
    pendingResolvers.delete(id)
    resolvers?.forEach(settler)
}

async function fetchAnswerBatch(ids: string[]) {
    let result
    try {
        result = await api.getAnswersWithMeta(ids, { skipCache: true })
    } catch (error) {
        ids.forEach(id => settle(id, r => r.reject(error)))
        return
    }

    if (!result.ok) {
        const error = new Error(
            `bulk answer fetch failed (status ${result.status ?? 'network error'})`,
        )
        ids.forEach(id => settle(id, r => r.reject(error)))
        return
    }

    const answersById = new Map<string, Answer>()
    for (const answer of result.answers || []) {
        if (answer?.id) answersById.set(String(answer.id), answer)
    }

    ids.forEach(id => {
        const fetched = answersById.get(id)
        const answer = fetched ? withPreservedSignalContext(fetched) : null
        if (answer) cache.cacheAnswer(answer)
        settle(id, r => r.resolve(answer))
    })
}

function flushAnswerBatchQueue() {
    flushTimer = null
    const ids = Array.from(queuedIds)
    queuedIds.clear()

    for (let index = 0; index < ids.length; index += MAX_BATCH_SIZE) {
        fetchAnswerBatch(ids.slice(index, index + MAX_BATCH_SIZE))
    }
}

// Drop-in replacement for api.getAnswer for callers that don't need an explicit
// quest context: resolves with the answer (already written to the entity cache)
// or null when the server omits it (not found / not visible), and rejects on
// transport errors — mirroring api.getAnswer's behavior.
export function getAnswerBatched(answerId: string | number | null | undefined) {
    const id = String(answerId || '')
    if (!id) return Promise.resolve<Answer | null>(null)

    return new Promise<Answer | null>((resolve, reject) => {
        const existing = pendingResolvers.get(id)
        if (existing) {
            existing.push({ resolve, reject })
            return
        }

        pendingResolvers.set(id, [{ resolve, reject }])
        queuedIds.add(id)
        if (!flushTimer) {
            flushTimer = setTimeout(flushAnswerBatchQueue, FLUSH_DELAY_MS)
        }
    })
}
